import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShoppingBag, Users, DollarSign, Package, Mail, BarChart3, Bell } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "@/lib/format";
import AdminAnalytics from "@/components/AdminAnalytics";
import AdminProductsTab from "@/components/admin/AdminProductsTab";
import AdminOrdersTab from "@/components/admin/AdminOrdersTab";
import AdminIntakesTab from "@/components/admin/AdminIntakesTab";
import AdminCustomersTab from "@/components/admin/AdminCustomersTab";

export default function Admin() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [waReminders, setWaReminders] = useState({ orders: [], repairs: [], threshold_hours: 24 });

  const loadAll = async () => {
    const [p, o, s, r, c, w] = await Promise.all([
      api.get("/products"),
      api.get("/admin/orders"),
      api.get("/admin/stats"),
      api.get("/admin/repair-intakes"),
      api.get("/admin/customers"),
      api.get("/admin/wa-reminders"),
    ]);
    setProducts(p.data); setOrders(o.data); setStats(s.data); setIntakes(r.data); setCustomers(c.data);
    setWaReminders(w.data);
  };

  useEffect(() => { loadAll(); }, []);

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const payload = testEmailTo.trim() ? { to: testEmailTo.trim() } : {};
      const { data } = await api.post("/admin/test-email", payload);
      toast.success(data.message || `Test email sent to ${data.recipient}`);
      setTestEmailOpen(false);
      setTestEmailTo("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <main data-testid="admin-page" className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Admin</div>
          <h1 className="mt-1 font-display font-extrabold text-3xl sm:text-4xl tracking-tighter text-zinc-900">Dashboard</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="test-email-btn" onClick={() => setTestEmailOpen(true)} variant="outline" className="rounded-full border-2 border-zinc-200 hover:border-zinc-900 bg-white text-zinc-900 px-5">
            <Mail className="w-4 h-4 mr-1.5" /> Send test email
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Revenue", value: formatIDR(stats.revenue), icon: DollarSign },
            { label: "Orders", value: stats.orders, icon: ShoppingBag },
            { label: "Products", value: stats.products, icon: Package },
            { label: "Customers", value: stats.users, icon: Users },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-zinc-100 p-5">
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{s.label}</div>
                <s.icon className="w-4 h-4 text-brand" />
              </div>
              <div className="mt-3 font-display font-extrabold text-2xl text-zinc-900">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {(waReminders.orders.length > 0 || waReminders.repairs.length > 0) && (
        <div data-testid="wa-reminder-banner" className="mb-8 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-start gap-3">
          <Bell className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">
              {waReminders.orders.length + waReminders.repairs.length} item(s) haven't had a WhatsApp update in {waReminders.threshold_hours}+ hours
            </div>
            <div className="text-xs text-amber-700 mt-0.5">
              {waReminders.orders.length > 0 && <button data-testid="wa-reminder-orders-link" onClick={() => setTab("orders")} className="underline hover:text-amber-900">{waReminders.orders.length} order(s)</button>}
              {waReminders.orders.length > 0 && waReminders.repairs.length > 0 && " · "}
              {waReminders.repairs.length > 0 && <button data-testid="wa-reminder-repairs-link" onClick={() => setTab("intakes")} className="underline hover:text-amber-900">{waReminders.repairs.length} repair(s)</button>}
              {" "}waiting on a customer nudge. A daily email digest is also sent to the owner.
            </div>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-zinc-100 rounded-full p-1 h-auto">
          <TabsTrigger data-testid="tab-products" value="products" className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Products</TabsTrigger>
          <TabsTrigger data-testid="tab-orders" value="orders" className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Orders {waReminders.orders.length > 0 && <span className="ml-2 text-[10px] font-bold bg-amber-500 text-white rounded-full px-1.5 py-0.5">{waReminders.orders.length}</span>}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-intakes" value="intakes" className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Repair Intakes {intakes.filter((i) => i.status === "new").length > 0 && <span className="ml-2 text-[10px] font-bold bg-brand text-white rounded-full px-1.5 py-0.5">{intakes.filter((i) => i.status === "new").length}</span>}
            {waReminders.repairs.length > 0 && <span className="ml-1.5 text-[10px] font-bold bg-amber-500 text-white rounded-full px-1.5 py-0.5">{waReminders.repairs.length}</span>}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-customers" value="customers" className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Users className="w-4 h-4 mr-1.5" /> Customers
          </TabsTrigger>
          <TabsTrigger data-testid="tab-analytics" value="analytics" className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <BarChart3 className="w-4 h-4 mr-1.5" /> Visitors
          </TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6">
          <AdminProductsTab products={products} setProducts={setProducts} loadAll={loadAll} />
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <AdminOrdersTab orders={orders} setOrders={setOrders} />
        </TabsContent>

        <TabsContent value="intakes" className="mt-6">
          <AdminIntakesTab intakes={intakes} setIntakes={setIntakes} />
        </TabsContent>

        <TabsContent value="customers" className="mt-6">
          <AdminCustomersTab customers={customers} setCustomers={setCustomers} />
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <AdminAnalytics />
        </TabsContent>
      </Tabs>

      <Dialog open={testEmailOpen} onOpenChange={setTestEmailOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Send test email</DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Sends a test message via your GoDaddy SMTP sender so you can verify delivery before customers see it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-zinc-800">Recipient (optional)</Label>
              <Input data-testid="test-email-input" value={testEmailTo} onChange={(e) => setTestEmailTo(e.target.value)} placeholder="you@example.com" className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
              <div className="mt-1.5 text-xs text-zinc-500">Leave blank to send to your admin email.</div>
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setTestEmailOpen(false)}>Cancel</Button>
            <Button data-testid="send-test-email-btn" onClick={sendTestEmail} disabled={sendingTest} className="rounded-full bg-brand hover:bg-brand-hover text-white">
              {sendingTest ? "Sending…" : "Send test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
